import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { of, switchMap, finalize, catchError } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PsychometricTestService } from '../../services/psychometric-test/psychometric-test.service';
import { UserSelectors } from '../../states/user/user-selector';
import { UserStateModel } from '../../states/user/user-state-model';
import { TestStatus, MatchReport } from '../../models/psychometric-test/psychometric-test.model';
import { TestType } from '../../models/enums/enums';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MindlerTestRequestService } from '../../services/psychometric-test/mindler-test-request.service';
import { MindlerAssessmentType, MindlerTestRequestResponse, MindlerTestRequestStatusEnum, MindlerTestType, OrderSummary, PaymentConfirmation } from '../../models/psychometric-test/psychometric-test.model';
import { PaymentConfirmationComponent } from './payment-confirmation/payment-confirmation.component';
import { BillingComponent } from './billing/billing.component';
import { AuthService } from '../../services/account/auth.service';
import { UserResponse } from '../../models/account/userResponse';
import { PackagesService } from '../../services/payments/packages/packages.service';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FormsModule } from '@angular/forms';
import { PackagesResponse } from '../../models/payment/packagesResponse';
import { PaymentService } from '../../services/payments/payment/payment.service';
import { TransactionDetailResponse } from '../../models/payment/transactionDetailResponse.model';
import { Router, ActivatedRoute } from '@angular/router';

enum PaymentStep {
  TestSelection = 'TestSelection',
  BillingInfo = 'BillingInfo',
  PaymentInfo = 'PaymentInfo',
  Confirmation = 'Confirmation',
  TestInProgress = 'TestInProgress',
  TestCompleted = 'TestCompleted'
}

enum UserTestStatus {
  TestSelection = 'TestSelection',
  RequestPending = 'RequestPending',
  RequestRejected = 'RequestRejected',
  RequestApproved = 'RequestApproved',
  Started = 'Started',
  NotStarted = 'NotStarted',
  Pending = 'Pending',
  Completed = 'Completed'
}

@Component({
  selector: 'app-psychometric-test',
  standalone: true,
  imports: [CommonModule, SkeletonModule, ButtonModule, ProgressSpinnerModule, BillingComponent, PaymentConfirmationComponent, CardModule, DividerModule, DialogModule, InputTextareaModule, FormsModule],
  templateUrl: './psychometric-test.component.html',
  styleUrls: ['./psychometric-test.component.scss']
})
export class PsychometricTestComponent implements OnInit, OnDestroy {
  // Enums
  UserTestStatus = UserTestStatus;
  testType = TestType;
  testStatus = TestStatus;

  // Signals
  mindlerTestRequest = signal<MindlerTestRequestResponse | null>(null);
  mindlerTestRequestLoading = signal<boolean>(false);

  // State
  userTestStatus: UserTestStatus = UserTestStatus.TestSelection;
  loading = false;
  testUrl!: SafeResourceUrl;
  videoUrl!: string;
  summaryReportUrl?: string;
  detailReportUrl?: string;
  streams: MatchReport[] = [];

  // Payment flow
  selectedTestType: MindlerTestType | null = null;
  orderSummary: OrderSummary | null = null;
  paymentConfirmation: PaymentConfirmation | null = null;
  currentStep: PaymentStep = PaymentStep.TestSelection;
  loadingOrderSummary = false;
  userDetails = signal<UserResponse | null>(null);

  // Analysis Dialog
  showAnalysisDialog = false;
  analysisMessage = '';
  analysisLoading = false;

  // Post-payment polling
  private justPaidPollingTimer: any = null;
  private justPaidPollAttempts = 0;
  private readonly MAX_POLL_ATTEMPTS = 10;
  justPaidPolling = false;

  // Landing page FAQ accordion
  openFaqIndex: number | null = 0;
  faqs = [
    {
      question: 'How long does the psychometric test take to complete?',
      answer: 'The test takes about 15 minutes to complete. It has around 60 questions about your personality, preferences, activities and subjects — there are no right or wrong answers, so just answer honestly.'
    },
    {
      question: 'Which test should I take — Stream or Career?',
      answer: 'If you are in Grade 9-10 and deciding your 11th grade subjects, take the Stream Assessment. If you are in Grade 11-12+ and want to zero in on a best-fit career, take the more comprehensive Career Assessment.'
    },
    {
      question: 'Is the test scientifically validated?',
      answer: 'Yes. Both assessments are built on the same scientifically validated framework, backed by research and trusted by top school counsellors and international experts.'
    },
    {
      question: 'What will I get in my report?',
      answer: 'You receive a detailed career blueprint with your personality profile, top career matches, recommended streams, colleges and a personal action plan — up to a 36-page report.'
    },
    {
      question: 'Do I get expert guidance after the test?',
      answer: 'Yes. One free expert-backed 1-on-1 counselling session is included so you can discuss your report and next steps with a mentor.'
    },
    {
      question: 'Can I retake the test?',
      answer: 'The assessment is designed to be taken once for the most accurate results. If you need to retake it, please reach out to your counsellor or our support team.'
    },
    {
      question: 'How accurate are the career matches?',
      answer: 'Your responses are cross-referenced against 56 evaluation parameters and mapped to 500+ careers, giving you a highly personalised and accurate set of best-fit matches.'
    }
  ];

  constructor(
    private testService: PsychometricTestService,
    private mindlerTestRequestService: MindlerTestRequestService,
    private store: Store,
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
    private packagesService: PackagesService,
    private authService: AuthService,
    private paymentService: PaymentService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  trackByStreamId(index: number, stream: MatchReport): number {
    return stream.id;
  }

  /** 'Stream' for a stream assessment, otherwise 'Career' — drives the completed-view heading. */
  get matchLabel(): string {
    return this.mindlerTestRequest()?.assessmentType === MindlerAssessmentType.Stream ? 'Stream' : 'Career';
  }

  backToDashboard(): void {
    this.router.navigate(['/portal/dashboard']);
  }

  ngOnInit(): void {
    this.videoUrl = 'https://yourblobstorage.blob.core.windows.net/videos/career-reel.mp4';
    const justPaid = this.route.snapshot.queryParamMap.get('justPaid');
    if (justPaid === 'true') {
      this.justPaidPolling = true;
      this.justPaidPollAttempts = 0;
      this.pollForApproval();
    } else {
      this.resolveUserTestStatus();
    }
  }

  ngOnDestroy(): void {
    if (this.justPaidPollingTimer) {
      clearTimeout(this.justPaidPollingTimer);
    }
  }


  resolveUserTestStatus(): void {
    this.loading = true;
    this.mindlerTestRequestLoading.set(true);

    this.mindlerTestRequestService.getTestRequest().pipe(
      switchMap((request: MindlerTestRequestResponse | null) => {
        this.mindlerTestRequest.set(request);

        if (!request) {
          this.userTestStatus = UserTestStatus.TestSelection;
          this.mindlerTestRequestLoading.set(false);
          this.loading = false;
          return of(null);
        }

        switch (request.status) {
          case MindlerTestRequestStatusEnum.Pending:
            // During justPaidPolling keep a neutral state; outside polling show the selection/billing page
            this.userTestStatus = this.justPaidPolling
              ? UserTestStatus.RequestPending
              : UserTestStatus.TestSelection;
            this.mindlerTestRequestLoading.set(false);
            this.loading = false;
            return of(null);

          case MindlerTestRequestStatusEnum.Rejected:
            this.mindlerTestRequestLoading.set(false);
            this.userTestStatus = UserTestStatus.RequestRejected;
            this.loading = false;
            return of(null);

          case MindlerTestRequestStatusEnum.Approved:
            this.userTestStatus = UserTestStatus.RequestApproved;
            return this.testService.isTestTaken().pipe(
              catchError(() => {
                this.loading = false;
                return of(null);
              })
            );
          default:
            this.mindlerTestRequestLoading.set(false);
            this.loading = false;
            return of(null);
        }
      }),
      finalize(() => {
        this.loading = false;
        this.mindlerTestRequestLoading.set(false);
      })
    ).subscribe((testStatusResponse: any) => {
      if (this.userTestStatus === UserTestStatus.RequestApproved && testStatusResponse) {
        switch (testStatusResponse.testStatus) {
          case TestStatus.NotStarted:
            this.userTestStatus = UserTestStatus.NotStarted;
            this.loadPaymentConfirmation();
            break;
          case TestStatus.Started:
            this.userTestStatus = UserTestStatus.Started;
            this.startTest();
            break;
          case TestStatus.Pending:
            this.userTestStatus = UserTestStatus.Pending;
            this.startTest();
            break;
          case TestStatus.Completed:
            this.userTestStatus = UserTestStatus.Completed;
            this.getTestReport();
            break;
        }
      }
      this.mindlerTestRequestLoading.set(false);
      this.loading = false;
    }, () => {
      this.mindlerTestRequestLoading.set(false);
      this.loading = false;
    });
  }

  private pollForApproval(): void {
    // Poll by calling resolveUserTestStatus(), which updates the mindlerTestRequest signal.
    // After each resolve, check if status is now Approved. Stop when approved or max attempts hit.
    const doOnePoll = () => {
      this.resolveUserTestStatus();
      this.justPaidPollAttempts++;

      // Give the async call 2.5 seconds to complete, then check the signal
      setTimeout(() => {
        const currentRequest = this.mindlerTestRequest();
        const isApproved = currentRequest?.status === MindlerTestRequestStatusEnum.Approved;
        const maxReached = this.justPaidPollAttempts >= this.MAX_POLL_ATTEMPTS;

        if (isApproved) {
          // Status is approved — resolveUserTestStatus already set the right view, just stop polling
          this.justPaidPolling = false;
          return;
        }

        if (maxReached) {
          this.justPaidPolling = false;
          this.userTestStatus = UserTestStatus.TestSelection;
          this.messageService.add({
            severity: 'warn',
            summary: 'Payment Processing',
            detail: 'Your payment is being processed. Please refresh in a moment.'
          });
          return;
        }

        // Schedule next poll after 3 seconds
        this.justPaidPollingTimer = setTimeout(doOnePoll, 3000);
      }, 2500);
    };

    // Start the first poll immediately
    this.justPaidPollingTimer = setTimeout(doOnePoll, 500);
  }

  loadPaymentConfirmation() {
    if (this.mindlerTestRequest()?.transactionNumber && this.mindlerTestRequest()?.transactionNumber !== '') {
      this.paymentService.getTransaction(this.mindlerTestRequest()?.transactionNumber as string).subscribe({
        next: (data: TransactionDetailResponse) => {
          this.paymentConfirmation = {
            orderId: data.transactionId,
            orderDate: data.paymentDate,
            billingInfo: {
              fullName: data.customerDetails.name,
              emailAddress: data.customerDetails.email,
            },
            accountInfo: {
              accountHolder: `${this.userDetails()?.firstName} ${this.userDetails()?.lastName || ''}`.trim(),
              registeredEmail: this.userDetails()?.email ?? '',
              registrationDate: data.paymentDate
            },
            orderSummary: {
              packageId: 0,
              testName: data.packageDetails.packageName,
              description: data.packageDetails.description || undefined,
              duration: '60 minutes',
              subtotal: data.paymentSummary.packageAmount,
              tax: data.paymentSummary.totalPaid - data.paymentSummary.packageAmount,
              total: data.paymentSummary.totalPaid,
              whatsIncluded: data.packageDetails.servicesIncluded
            },
            paymentMethod: 'Online Payment',
            paymentDate: data.paymentDate
          } as PaymentConfirmation;
        },
        error: () => {
          this.showError('Failed to load payment confirmation');
        }
      });
    }
  }


  private createTestRequest(type: MindlerTestType): void {
    this.mindlerTestRequestLoading.set(true);
    this.mindlerTestRequestService.createTestRequest(type).pipe(
      catchError((error: any) => {
        this.showError('Failed to create test request. Please try again.');
        this.mindlerTestRequestLoading.set(false);
        this.currentStep = PaymentStep.TestSelection;
        this.selectedTestType = null;
        this.orderSummary = null;
        return of(null);
      })
    ).subscribe({
      next: (response: MindlerTestRequestResponse | null) => {
        if (response) {
          this.mindlerTestRequest.set(response);
          // After successful request, show billing component
          if (response.status === MindlerTestRequestStatusEnum.Pending) {
            this.userTestStatus = UserTestStatus.RequestPending;
            this.loadOrderSummaryForTest(type);
          }
        }
        this.mindlerTestRequestLoading.set(false);
      }
    });
  }

  private loadOrderSummaryForTest(type: MindlerTestType): void {
    this.loadingOrderSummary = true;
    const packageName = 'Psychometric Test';

    this.packagesService.getPackageBySystemAssignedName(packageName).pipe(
      catchError((error: any) => {
        console.warn('Failed to fetch package details from API, using fallback:', error);
        this.loadingOrderSummary = false;
        return of(null);
      })
    ).subscribe({
      next: (packageResponse: PackagesResponse | null) => {
        this.loadingOrderSummary = false;
        if (packageResponse) {
          this.orderSummary = this.mapPackageToOrderSummary(packageResponse, type);
        }
      }
    });
  }

  private mapPackageToOrderSummary(packageResponse: PackagesResponse, type: MindlerTestType): OrderSummary {
    const durationMonths = packageResponse.duration ?? 1;
    const durationText =
      durationMonths === 1
        ? '1 Month'
        : `${durationMonths} Months`;

    const subtotal = packageResponse.amount || 0;
    const tax = +(subtotal * 0.18).toFixed(2);
    const total = +(subtotal + tax).toFixed(2);

    return {
      packageId: packageResponse.id || 0,
      testName: `Comprehensive Psychometric Assessment - ${type === 1 ? 'Stream' : 'Career'} Test`,
      description: packageResponse.description || '',
      duration: durationText,
      subtotal,
      tax,
      total,
      whatsIncluded: packageResponse.servicesIncluded || [],
      type
    };
  }

  requestTest(type: MindlerTestType): void {
    const existingRequest = this.mindlerTestRequest();
    if (existingRequest && existingRequest.status === MindlerTestRequestStatusEnum.Pending && existingRequest.assessmentType === type) {
      this.userTestStatus = UserTestStatus.RequestPending;
      this.loadOrderSummaryForTest(type);
      return;
    }
    this.createTestRequest(type);
  }

  startTest(): void {
    this.loading = true;
    this.testService.startTest().subscribe({
      next: (res) => {
        this.userTestStatus = UserTestStatus.Started;
        this.testUrl = this.sanitizer.bypassSecurityTrustResourceUrl(res.testUrl);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showError('Failed to start test');
      }
    });
  }

  private loadUserInfo(): void {
    this.store.selectOnce(UserSelectors.getUser).subscribe((user: UserStateModel) => {
      if (user?.id) {
        this.loadUserDetailsById(user.id);
      }
    });
  }

  private loadUserDetailsById(userId: string): void {
    this.authService.getUserByid(userId).subscribe({
      next: (userDetails: UserResponse) => {
        this.userDetails.set(userDetails);
      },
      error: () => {
        this.userDetails.set(null);
      }
    });
  }

  openAnalysisDialog(): void {
    this.showAnalysisDialog = true;
    this.analysisMessage = '';
  }

  submitAnalysisRequest(): void {
    const user = this.userDetails();
    if (!user?.id) {
      this.showError('User information not available');
      return;
    }

    if (!this.analysisMessage.trim()) {
      this.showError('Please provide more details for the analysis request');
      return;
    }

    this.analysisLoading = true;
    this.testService.createRequestReportAnalysis(user.id, { message: this.analysisMessage }).pipe(
      finalize(() => this.analysisLoading = false)
    ).subscribe({
      next: () => {
        this.showSuccess('Request Submitted', 'Your report analysis request has been submitted successfully.');
        this.showAnalysisDialog = false;
      },
      error: () => {
        this.showError('Failed to submit analysis request');
      }
    });
  }

  getTestReport(): void {
    this.store.selectOnce(UserSelectors.getUser).subscribe((user: UserStateModel) => {
      if (!user?.id) {
        this.showError('User information not available');
        return;
      }

      this.loading = true;
      this.testService.getSummaryReport(user.id).pipe(
        switchMap((summary) => {
          this.summaryReportUrl = summary?.reportUrl ?? '';
          return this.testService.getDetailReport(user.id);
        }),
        switchMap((detail) => {
          this.detailReportUrl = detail?.reportUrl ?? '';
          return this.testService.getMatchReport(user.id);
        }),
        catchError(() => {
          this.loading = false;
          this.showError('Failed to fetch reports');
          return of(null);
        })
      ).subscribe({
        next: (match: any) => {
          this.streams = match?.data ?? [];
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.showError('Failed to fetch reports');
        }
      });
    });
  }

  openUrl(url: string): void {
    window.open(url, '_blank');
  }

  scrollToId(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  private showError(message: string = 'Error while processing request'): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }

  private showSuccess(summary: string, detail: string): void {
    this.messageService.add({ severity: 'success', summary, detail });
  }
}
